import { FC, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useThemeColors } from 'hooks/useThemeColors'

/* Components */
import { Wrapper } from './insightsWidget.styles'
import DetailsCard from './components/DetailsCard'
import UnreadInsights from './components/UnreadInsights'
import { InsightSubType } from '../../../../../models'

const InfoIcon: FC<{ stroke: string }> = ({ stroke }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 10.5V14.875M24.5 14C24.5 19.799 19.799 24.5 14 24.5C8.20101 24.5 3.5 19.799 3.5 14C3.5 8.20101 8.20101 3.5 14 3.5C19.799 3.5 24.5 8.20101 24.5 14ZM14 18.375H14.0088V18.3838H14V18.375Z"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const OptimizationIcon: FC<{ stroke: string }> = ({ stroke }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.25 7L23.625 7M8.75 7C8.75 6.0335 9.5335 5.25 10.5 5.25C11.4665 5.25 12.25 6.0335 12.25 7C12.25 7.9665 11.4665 8.75 10.5 8.75C9.5335 8.75 8.75 7.9665 8.75 7ZM4.375 7H8.75M12.25 21H23.625M8.75 21C8.75 20.0335 9.5335 19.25 10.5 19.25C11.4665 19.25 12.25 20.0335 12.25 21C12.25 21.9665 11.4665 22.75 10.5 22.75C9.5335 22.75 8.75 21.9665 8.75 21ZM4.375 21L8.75 21M19.25 14L23.625 14M15.75 14C15.75 13.0335 16.5335 12.25 17.5 12.25C18.4665 12.25 19.25 13.0335 19.25 14C19.25 14.9665 18.4665 15.75 17.5 15.75C16.5335 15.75 15.75 14.9665 15.75 14ZM4.375 14H15.75"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const PerformanceIcon: FC<{ stroke: string }> = ({ stroke }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.738 11.958C8.67874 11.958 9.52756 11.4379 10.1081 10.6976C11.0101 9.54749 12.147 8.5903 13.4465 7.89826C14.289 7.44958 15.02 6.783 15.3745 5.89675C15.6226 5.27662 15.75 4.61485 15.75 3.94695V3.20801C15.75 2.72476 16.1418 2.33301 16.625 2.33301C18.0747 2.33301 19.25 3.50826 19.25 4.95801C19.25 6.30158 18.9472 7.57441 18.4061 8.712C18.0962 9.36339 18.5309 10.208 19.2522 10.208M19.2522 10.208H22.899C24.0968 10.208 25.1689 11.0177 25.2957 12.2087C25.3481 12.7013 25.375 13.2015 25.375 13.708C25.375 17.0302 24.2178 20.0822 22.2845 22.4828C21.8323 23.0443 21.1332 23.333 20.4123 23.333H15.727C15.1627 23.333 14.6021 23.242 14.0668 23.0636L10.4332 21.8524C9.89791 21.674 9.33732 21.583 8.77304 21.583H6.88824M19.2522 10.208H16.625M6.88824 21.583C6.98485 21.8217 7.08999 22.056 7.20327 22.2856C7.43326 22.7517 7.11223 23.333 6.59245 23.333H5.53337C4.49654 23.333 3.5349 22.7287 3.23232 21.737C2.83743 20.4427 2.625 19.0689 2.625 17.6455C2.625 15.8343 2.96894 14.1034 3.59507 12.5146C3.95097 11.6115 4.86189 11.083 5.83258 11.083H7.0608C7.61138 11.083 7.93019 11.7316 7.64435 12.2021C6.6802 13.7894 6.125 15.6526 6.125 17.6455C6.125 19.0379 6.39604 20.3671 6.88824 21.583Z"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

interface Props {
  unreadInsightsCount: number
  insightsCountLoading: boolean
  insightsData: any
  isLoading?: boolean
}

const InsightsWidget: FC<Props> = ({ insightsData, unreadInsightsCount, insightsCountLoading, isLoading }) => {
  const navigate = useNavigate()
  const getColor = useThemeColors()
  const iconStroke = getColor('cta')
  const showUnread = !insightsCountLoading && unreadInsightsCount > 0

  const items = useMemo(() => {
    return [
      {
        icon: <InfoIcon stroke={iconStroke} />,
        value: insightsData?.performanceDegradation || 0,
        text: 'Declining Trends'
      },
      {
        icon: <OptimizationIcon stroke={iconStroke} />,
        value: insightsData?.suggestedOptimization || 0,
        text: 'Suggested Optimization'
      },
      {
        icon: <PerformanceIcon stroke={iconStroke} />,
        value: insightsData?.performanceImprovement || 0,
        text: 'Improving Trends'
      }
    ]
  }, [insightsData, iconStroke])

  const handleCardClick = (text: string) => {
    const types =
      text === 'Declining Trends'
        ? [InsightSubType.Reduction, InsightSubType.CrossedIntoRed]
        : text === 'Suggested Optimization'
        ? [InsightSubType.CpiSlaOptimization]
        : [InsightSubType.Improvement, InsightSubType.CrossedIntoGreen]
    navigate('/insights', { state: { types } })
  }

  return (
    <Wrapper className="insights-widget-wrapper" showUnread={showUnread ? 'true' : 'false'}>
      <Box className="header">
        <Box className="header-start">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8 12V8.5M8 8.5C8.3453 8.5 8.68038 8.45629 9 8.37402M8 8.5C7.6547 8.5 7.31962 8.45629 7 8.37402M9.5 13.3596C9.01413 13.4518 8.5127 13.5 8 13.5C7.48731 13.5 6.98588 13.4518 6.5 13.3596M9 14.948C8.67136 14.9824 8.33774 15 8 15C7.66226 15 7.32864 14.9824 7 14.948M9.5 12V11.8722C9.5 11.2169 9.93885 10.6567 10.5056 10.3278C11.997 9.4625 13 7.84838 13 6C13 3.23858 10.7614 1 8 1C5.23858 1 3 3.23858 3 6C3 7.84838 4.00297 9.4625 5.49437 10.3278C6.06115 10.6567 6.5 11.2169 6.5 11.8722V12"
              stroke={getColor('menu-item-text')}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Typography className="header-title">OnyxAI Insights</Typography>
        </Box>
        <Typography className="header-date">Last 30 days</Typography>
      </Box>
      {showUnread && <UnreadInsights value={unreadInsightsCount} />}
      <Box className="content">
        {items.map((item) => (
          <DetailsCard key={item.text} {...item} handleClick={handleCardClick} isLoading={isLoading} />
        ))}
      </Box>
    </Wrapper>
  )
}

export default InsightsWidget
